/*
 * `/forgot-password` (flow F6). One address in, and one answer out whatever the address: Better Auth
 * answers the same for an account and for none, and the email goes only to an account that exists and
 * is not banned. The link it carries opens `/reset-password`.
 */
import { useEffect, useState } from "react";
import { type ForgotOutcome, forgotOutcome, LIMITED, UNREACHABLE } from "./account-copy.ts";
import { AuthFrame, postJson } from "./auth-frame.tsx";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ForgotOutcome | null>(null);
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (outcome?.kind !== "limited") return;
    setWaiting(true);
    const timer = setTimeout(() => setWaiting(false), outcome.retryAfterMs);
    return () => clearTimeout(timer);
  }, [outcome]);

  if (outcome?.kind === "sent")
    return (
      <AuthFrame tab="Reset your password" title="Check your email">
        <p className="lcr-signin-sub" role="status">
          If an account exists for <strong>{email}</strong>, we sent it a link to choose a new
          password. The link works for 60 minutes.
        </p>
        <div className="lcr-signin-form">
          <p className="lcr-auth-hint">
            Nothing after a few minutes? Check spam, or{" "}
            <button type="button" className="lcr-linklike" onClick={() => setOutcome(null)}>
              try another address
            </button>
            .
          </p>
          <a className="lcr-linklike" href="/">
            Back to sign in
          </a>
        </div>
      </AuthFrame>
    );

  const failure =
    outcome?.kind === "limited"
      ? LIMITED
      : outcome?.kind === "unreachable"
        ? UNREACHABLE
        : outcome?.kind === "invalid"
          ? "Check the email address. It is the one you sign in with."
          : null;
  return (
    <AuthFrame
      tab="Reset your password"
      title="Reset your password"
      sub="Enter the email you sign in with. We will send you a link to choose a new password."
    >
      <form
        className="lcr-signin-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setOutcome(null);
          const answer = await postJson("/api/auth/request-password-reset", {
            email: email.trim(),
          });
          setOutcome(forgotOutcome(answer.status, answer.retryAfter));
          setBusy(false);
        }}
      >
        <label className="lcr-signin-field">
          Email
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            aria-invalid={outcome?.kind === "invalid" || undefined}
            aria-describedby={failure ? "lcr-forgot-failure" : undefined}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        {failure && (
          <p id="lcr-forgot-failure" className="lcr-signin-alert" role="alert">
            {failure}
          </p>
        )}
        <button className="lcr-primary lcr-signin-submit" type="submit" disabled={busy || waiting}>
          {busy ? "Sending…" : "Send the link"}
        </button>
        <a className="lcr-linklike" href="/">
          Back to sign in
        </a>
      </form>
    </AuthFrame>
  );
}
